import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { 
  Database, 
  Search, 
  ArrowLeft, 
  ArrowRight,
  CheckCircle,
  Users,
  Building,
  ChevronsUpDown,
  X,
  Check,
  User
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { examQuestionsAPI, examAssignmentsAPI, candidatesAPI2 } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const QuestionBankSelectionModal = ({
  isOpen,
  onClose,
  onQuestionsSelected,
  eligibleCandidates = [], // Pre-calculated eligible candidates from AIModule
  questionGenerationChoice = null, // Add this prop to handle existing/new choice
}) => {
  const [currentStep, setCurrentStep] = useState("banks"); // "banks", "questions", or "candidates"
  const [selectedBankIds, setSelectedBankIds] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [questionCandidateAssignments, setQuestionCandidateAssignments] = useState({}); // { questionId: [candidateId1, candidateId2] }
  const [searchTerm, setSearchTerm] = useState("");
  const [questionSearchTerm, setQuestionSearchTerm] = useState("");
  const [questionPage, setQuestionPage] = useState(1);
  const [bankPage, setBankPage] = useState(1);
  const banksPerPage = 8; // 4x2 grid for folder-style cards
  const { toast } = useToast();

  // Query for question banks
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["exam-question-categories"],
    queryFn: () => examQuestionsAPI.getCategories(),
    enabled: isOpen,
  });

  // Query for questions from selected banks
  const { data: questionsResponse, isLoading: questionsLoading } = useQuery({
    queryKey: ["bank-questions", selectedBankIds, questionSearchTerm, questionPage],
    queryFn: () => {
      return examQuestionsAPI.getQuestions({
        page: questionPage,
        limit: 10,
        questionBankIds: selectedBankIds,
        search: questionSearchTerm,
      });
    },
    enabled: isOpen && currentStep === "questions" && selectedBankIds.length > 0,
    keepPreviousData: true,
  });

  // Extract data from axios response
  const questionsData = questionsResponse?.data;

  // Use pre-calculated eligible candidates instead of fetching from API
  const processedCandidatesData = {
    candidates: eligibleCandidates,
    totalPages: 1,
    total: eligibleCandidates.length
  };

  // Debug logging
  useEffect(() => {
    if (questionsResponse) {
      console.log('Questions API Response:', questionsResponse);
      console.log('Questions Data:', questionsData);
    }
  }, [questionsResponse, questionsData, eligibleCandidates, processedCandidatesData]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep("banks");
      setSelectedBankIds([]);
      setSelectedQuestions([]);
      setSelectedCandidates([]);
      setQuestionCandidateAssignments({});
      setSearchTerm("");
      setQuestionSearchTerm("");
      setQuestionPage(1);
      setBankPage(1);
    }
  }, [isOpen]);

  // Reset question page when switching to questions step
  useEffect(() => {
    if (currentStep === "questions") {
      setQuestionPage(1);
    }
  }, [currentStep, selectedBankIds]);

  // Filter question banks based on search
  const getFilteredBanks = (banks) => {
    if (!searchTerm) return banks;
    return banks.filter(bank => 
      bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bank.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get all available question banks with pagination
  const userQuestionBanks = categoriesData?.data?.userQuestionBanks || [];
  const companyQuestionBanks = categoriesData?.data?.companyQuestionBanks || [];
  
  const allBanks = [...userQuestionBanks, ...companyQuestionBanks];
  const filteredBanks = getFilteredBanks(allBanks);
  
  // Pagination for banks
  const totalBankPages = Math.ceil(filteredBanks.length / banksPerPage);
  const startIndex = (bankPage - 1) * banksPerPage;
  const endIndex = startIndex + banksPerPage;
  const paginatedBanks = filteredBanks.slice(startIndex, endIndex);

  // Handle question bank selection
  const handleBankToggle = (bankId) => {
    setSelectedBankIds(prev => {
      if (prev.includes(bankId)) {
        return prev.filter(id => id !== bankId);
      } else {
        return [...prev, bankId];
      }
    });
  };

  // Handle question selection
  const handleQuestionToggle = (question) => {
    setSelectedQuestions(prev => {
      const exists = prev.find(q => q._id === question._id);
      if (exists) {
        return prev.filter(q => q._id !== question._id);
      } else {
        return [...prev, question];
      }
    });
  };

  // Proceed to questions step
  const handleProceedToQuestions = () => {
    if (selectedBankIds.length === 0) {
      toast({
        title: "No Question Banks Selected",
        description: "Please select at least one question bank to proceed.",
        variant: "destructive",
      });
      return;
    }
    setCurrentStep("questions");
    setQuestionPage(1);
  };

  // Go back to banks step
  const handleBackToBanks = () => {
    setCurrentStep("banks");
    setSelectedQuestions([]);
    setQuestionSearchTerm("");
    setQuestionPage(1);
  };

  // Proceed to candidates step
  const handleProceedToCandidates = () => {
    if (selectedQuestions.length === 0) {
      toast({
        title: "No Questions Selected",
        description: "Please select at least one question to assign.",
        variant: "destructive",
      });
      return;
    }
    setCurrentStep("candidates");
    setCandidatePage(1);
  };

  // Go back to questions step
  const handleBackToQuestions = () => {
    setCurrentStep("questions");
    setSelectedCandidates([]);
  };

  // Handle question-candidate assignment change from multi-select dropdown
  const handleQuestionCandidateChange = (questionId, selectedCandidateIds) => {
    setQuestionCandidateAssignments(prev => ({
      ...prev,
      [questionId]: selectedCandidateIds
    }));
  };

  // Handle individual question-candidate assignment
  const handleQuestionCandidateToggle = (questionId, candidateId) => {
    setQuestionCandidateAssignments(prev => {
      const currentAssignments = prev[questionId] || [];
      const isAssigned = currentAssignments.includes(candidateId);
      
      if (isAssigned) {
        // Remove assignment
        return {
          ...prev,
          [questionId]: currentAssignments.filter(id => id !== candidateId)
        };
      } else {
        // Add assignment
        return {
          ...prev,
          [questionId]: [...currentAssignments, candidateId]
        };
      }
    });
  };

  // Check if a question is assigned to a specific candidate
  const isQuestionAssignedToCandidate = (questionId, candidateId) => {
    const assignments = questionCandidateAssignments[questionId] || [];
    return assignments.includes(candidateId);
  };

  // Get assigned candidates for a specific question (only eligible ones)
  const getAssignedCandidatesForQuestion = (questionId) => {
    const assignedCandidateIds = questionCandidateAssignments[questionId] || [];
    return processedCandidatesData?.candidates?.filter(candidate => 
      assignedCandidateIds.includes(candidate._id)
    ) || [];
  };

  // Select all eligible candidates for a specific question
  const handleSelectAllCandidatesForQuestion = (questionId) => {
    if (processedCandidatesData?.candidates) {
      const eligibleCandidateIds = processedCandidatesData.candidates.map(c => c._id);
      setQuestionCandidateAssignments(prev => ({
        ...prev,
        [questionId]: eligibleCandidateIds
      }));
    }
  };

  // Clear all candidates for a specific question
  const handleClearCandidatesForQuestion = (questionId) => {
    setQuestionCandidateAssignments(prev => ({
      ...prev,
      [questionId]: []
    }));
  };

  // Get total number of assignments across all questions
  const getTotalAssignments = () => {
    return Object.values(questionCandidateAssignments).reduce((total, assignments) => {
      return total + (assignments?.length || 0);
    }, 0);
  };

  // Final assignment of questions to candidates
  const handleFinalAssignment = async () => {
    const totalAssignments = getTotalAssignments();
    
    if (totalAssignments === 0) {
      toast({
        title: "No Assignments Made",
        description: "Please assign at least one question to at least one candidate.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create assignment data structure
      const assignmentData = {
        questions: selectedQuestions,
        assignments: questionCandidateAssignments,
        candidateDetails: processedCandidatesData?.candidates || []
      };
      
      // Call the parent callback with the assignment data
      onQuestionsSelected(assignmentData);
      
      onClose();
      
      toast({
        title: "Questions Assigned Successfully",
        description: `${totalAssignments} question-candidate assignments created successfully.`,
      });
    } catch (error) {
      toast({
        title: "Assignment Failed",
        description: "There was an error assigning questions to candidates.",
        variant: "destructive",
      });
    }
  };

  // Assign selected questions (old function - now redirects to candidates)
  const handleAssignQuestions = () => {
    handleProceedToCandidates();
  };

  // Get selected bank names for display
  const getSelectedBankNames = () => {
    const allBanks = [...userQuestionBanks, ...companyQuestionBanks];
    return selectedBankIds.map(id => {
      const bank = allBanks.find(b => b._id === id);
      return bank ? bank.name : 'Unknown Bank';
    }).join(', ');
  };

  // Get bank type and icon
  const getBankTypeInfo = (bank) => {
    const isUserBank = userQuestionBanks.some(b => b._id === bank._id);
    return {
      type: isUserBank ? 'user' : 'company',
      icon: isUserBank ? Users : Building,
      color: isUserBank ? 'text-green-600' : 'text-purple-600'
    };
  };

  // Render question bank selection step
  const renderBankSelection = () => (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center gap-2 flex-shrink-0">
        <Database className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold">Select Question Banks</h3>
      </div>

      {/* Search Input */}
      <div className="relative flex-shrink-0">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search question banks..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setBankPage(1); // Reset to first page when searching
          }}
          className="pl-10"
        />
      </div>

      {/* Loading State */}
      {categoriesLoading && (
        <div className="text-center py-8 flex-1 flex items-center justify-center">
          <div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading question banks...</p>
          </div>
        </div>
      )}

      {/* Question Banks Grid - Folder-style Cards */}
      {!categoriesLoading && (
        <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
          {paginatedBanks.length > 0 ? (
            <>
              {/* Add top spacing and folder-style grid */}
              <div className="pt-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {paginatedBanks.map((bank) => {
                    const typeInfo = getBankTypeInfo(bank);
                    const IconComponent = typeInfo.icon;
                    const isSelected = selectedBankIds.includes(bank._id);
                    
                    return (
                      <Card 
                        key={bank._id} 
                        className={`cursor-pointer transition-all duration-200 hover:shadow-lg relative overflow-visible ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50 shadow-md' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleBankToggle(bank._id)}
                      >
                        {/* Selection Indicator */}
                        {isSelected && (
                          <div className="absolute -top-2 -right-2 z-10">
                            <div className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                              ✓
                            </div>
                          </div>
                        )}
                        
                        <CardContent className="p-4 text-center">
                          {/* Folder Icon - Clean without badge */}
                          <div className="flex justify-center mb-3">
                            <Database className={`h-12 w-12 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                          </div>
                          
                          {/* Bank Name */}
                          <h5 className="font-semibold text-sm mb-2 line-clamp-2 min-h-[2.5rem] flex items-center justify-center">
                            {bank.name}
                          </h5>
                          
                          {/* Bank Type */}
                          <div className="flex items-center justify-center gap-1 mb-2">
                            <IconComponent className={`h-3 w-3 ${typeInfo.color}`} />
                            <span className={`text-xs font-medium ${typeInfo.color}`}>
                              {typeInfo.type === 'user' ? 'Personal' : 'Company'}
                            </span>
                          </div>
                          
                          {/* Question Count */}
                          <p className="text-xs text-gray-600">
                            {bank.questionCount || 0} questions
                          </p>
                          
                          {/* Category Badge */}
                          {bank.category && (
                            <div className="mt-2">
                              <Badge variant="outline" className="text-xs">
                                {bank.category}
                              </Badge>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Pagination */}
              {totalBankPages > 1 && (
                <div className="flex justify-center pt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setBankPage(prev => Math.max(1, prev - 1))}
                          className={bankPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-gray-100"}
                        />
                      </PaginationItem>
                      {Array.from({ length: totalBankPages }, (_, i) => i + 1).map((page) => (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => setBankPage(page)}
                            isActive={page === bankPage}
                            className={`cursor-pointer ${
                              page === bankPage 
                                ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' 
                                : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setBankPage(prev => Math.min(totalBankPages, prev + 1))}
                          className={bankPage === totalBankPages ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-gray-100"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Database className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm ? "No question banks found matching your search." : "No question banks available."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Selected Banks Summary - Fixed at bottom */}
      {selectedBankIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
          <h4 className="font-medium text-blue-900 mb-1">Selected Question Banks ({selectedBankIds.length})</h4>
          <p className="text-sm text-blue-700 line-clamp-2">{getSelectedBankNames()}</p>
        </div>
      )}
    </div>
  );

  // Render question selection step
  const renderQuestionSelection = () => (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold">Select Questions</h3>
        </div>
        <Button variant="outline" size="sm" onClick={handleBackToBanks}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Banks
        </Button>
      </div>

      {/* Selected Banks Info */}
      <div className="bg-gray-50 border rounded-lg p-3 flex-shrink-0">
        <p className="text-sm text-gray-600">
          <span className="font-medium">Selected Banks:</span> {getSelectedBankNames()}
        </p>
      </div>

      {/* Search Input */}
      <div className="relative flex-shrink-0">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search questions..."
          value={questionSearchTerm}
          onChange={(e) => setQuestionSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Loading State */}
      {questionsLoading && (
        <div className="text-center py-8 flex-1 flex items-center justify-center">
          <div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading questions...</p>
          </div>
        </div>
      )}

      {/* Questions List - Scrollable */}
      {!questionsLoading && questionsData && questionsData.questions && (
        <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
          {questionsData.questions.length > 0 ? (
            <>
              <div className="space-y-3">
                {questionsData.questions.map((question) => (
                  <Card 
                    key={question._id} 
                    className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedQuestions.find(q => q._id === question._id)
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleQuestionToggle(question)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox 
                              checked={!!selectedQuestions.find(q => q._id === question._id)}
                              onChange={() => handleQuestionToggle(question)}
                            />
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {question.qtyp?.replace('_', ' ')}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {question.difficultyLevel}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                            {question.question}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{question.category}</span>
                            {question.subCategory && <span>• {question.subCategory}</span>}
                            <span>• {question.questionBankId?.name}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {questionsData && questionsData.totalPages > 1 && (
                <div className="flex justify-center pt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setQuestionPage(prev => Math.max(1, prev - 1))}
                          className={questionPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-gray-100"}
                        />
                      </PaginationItem>
                      
                      {/* Show first page if not in range */}
                      {questionPage > 3 && (
                        <>
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setQuestionPage(1)}
                              className="cursor-pointer hover:bg-gray-100 text-gray-700"
                            >
                              1
                            </PaginationLink>
                          </PaginationItem>
                          {questionPage > 4 && (
                            <PaginationItem>
                              <span className="px-3 py-2 text-gray-500">...</span>
                            </PaginationItem>
                          )}
                        </>
                      )}
                      
                      {/* Show pages around current page */}
                      {Array.from({ length: Math.min(5, questionsData.totalPages) }, (_, i) => {
                        const startPage = Math.max(1, Math.min(questionPage - 2, questionsData.totalPages - 4));
                        const page = startPage + i;
                        if (page > questionsData.totalPages) return null;
                        
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => setQuestionPage(page)}
                              isActive={page === questionPage}
                              className={`cursor-pointer ${
                                page === questionPage 
                                  ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' 
                                  : 'hover:bg-gray-100 text-gray-700'
                              }`}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      
                      {/* Show last page if not in range */}
                      {questionPage < questionsData.totalPages - 2 && (
                        <>
                          {questionPage < questionsData.totalPages - 3 && (
                            <PaginationItem>
                              <span className="px-3 py-2 text-gray-500">...</span>
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setQuestionPage(questionsData.totalPages)}
                              className="cursor-pointer hover:bg-gray-100 text-gray-700"
                            >
                              {questionsData.totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setQuestionPage(prev => Math.min(questionsData?.totalPages || 1, prev + 1))}
                          className={questionPage === (questionsData?.totalPages || 1) ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-gray-100"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Database className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {questionSearchTerm ? "No questions found matching your search." : "No questions available in selected banks."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* No Data State - when API hasn't returned data yet or returned empty */}
      {!questionsLoading && !questionsResponse && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8">
            <Database className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              Select question banks and search to load questions.
            </p>
          </div>
        </div>
      )}

      {/* Error State - when questionsData exists but doesn't have expected structure */}
      {!questionsLoading && questionsResponse && (!questionsData || !questionsData.questions) && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8">
            <Database className="h-12 w-12 text-red-300 mx-auto mb-4" />
            <p className="text-red-500 font-medium mb-2">Error loading questions</p>
            <p className="text-gray-500 text-sm">
              There was an issue loading questions from the selected banks. Please try again.
            </p>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-xs text-gray-400">Debug Info</summary>
                <pre className="text-xs text-gray-400 mt-2 bg-gray-100 p-2 rounded">
                  {JSON.stringify({ questionsResponse, questionsData }, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Selected Questions Summary - Fixed at bottom */}
      {selectedQuestions.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex-shrink-0">
          <h4 className="font-medium text-blue-900 mb-2">Selected Questions ({selectedQuestions.length})</h4>
          <p className="text-sm text-blue-700">
            {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''} ready to be assigned to eligible candidates.
          </p>
        </div>
      )}
    </div>
  );

  // Render candidate selection step
  const renderCandidateSelection = () => (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold">Assign Questions to Candidates</h3>
        </div>
        <Button variant="outline" size="sm" onClick={handleBackToQuestions}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Questions
        </Button>
      </div>

      {/* Selected Questions with Individual Candidate Assignment */}
      <div className="flex-1 overflow-y-auto space-y-6 min-h-0">
        {selectedQuestions.map((question, questionIndex) => (
          <Card key={question._id} className="border-2 border-gray-200">
            <CardContent className="p-6">
              {/* Question Header */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-xs">
                    {question.qtyp?.replace('_', ' ')}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {question.difficultyLevel}
                  </Badge>
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                    Question {questionIndex + 1}
                  </Badge>
                </div>
                <p className="text-sm font-medium text-gray-900 mb-2 line-clamp-3">
                  {question.question}
                </p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>{question.category}</span>
                  {question.subCategory && <span>• {question.subCategory}</span>}
                  <span>• {question.questionBankId?.name}</span>
                </div>
              </div>

              {/* Candidate Assignment Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Assign This Question To:</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectAllCandidatesForQuestion(question._id)}
                      className="text-xs"
                    >
                      <Users className="h-3 w-3 mr-1" />
                      All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleClearCandidatesForQuestion(question._id)}
                      className="text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                {/* Candidates Multi-Select Dropdown for this Question */}
                {processedCandidatesData && processedCandidatesData.candidates && processedCandidatesData.candidates.length > 0 ? (
                  <div className="space-y-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between min-h-10 h-auto py-2"
                        >
                          <div className="flex flex-wrap gap-1 max-w-[calc(100%-40px)] items-start">
                            <span className={cn(
                              "py-1 text-left",
                              getAssignedCandidatesForQuestion(question._id).length === 0 ? "text-muted-foreground" : ""
                            )}>
                              {getAssignedCandidatesForQuestion(question._id).length === 0
                                ? "Select candidates for this question..."
                                : getAssignedCandidatesForQuestion(question._id).length === 1
                                ? getAssignedCandidatesForQuestion(question._id)[0]?.name
                                : `${getAssignedCandidatesForQuestion(question._id).length} candidates selected`
                              }
                            </span>
                          </div>
                          <div className="flex items-start gap-1 shrink-0 ml-2 mt-1">
                            {getAssignedCandidatesForQuestion(question._id).length > 0 && (
                              <X 
                                className="h-4 w-4 opacity-50 hover:opacity-100" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClearCandidatesForQuestion(question._id);
                                }}
                              />
                            )}
                            <ChevronsUpDown className="h-4 w-4 opacity-50" />
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent 
                        className="w-full p-0" 
                        style={{ width: "max(var(--radix-popover-trigger-width), 320px)" }}
                        side="bottom"
                        align="start"
                        sideOffset={4}
                      >
                        <div className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
                          <div className="max-h-80 overflow-y-auto scrollbar-hidden">
                            <div className="p-1">
                              {/* Search Input */}
                              <div className="p-2 border-b mb-1 sticky top-0 bg-popover z-10">
                                <Input
                                  placeholder="Search candidates..."
                                  className="h-8"
                                />
                              </div>

                              {/* Individual Candidates */}
                              {processedCandidatesData.candidates.map((candidate) => (
                                <div
                                  key={candidate._id}
                                  onClick={() => handleQuestionCandidateToggle(question._id, candidate._id)}
                                  className="relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer"
                                >
                                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                    <Check
                                      className={cn(
                                        "h-4 w-4",
                                        isQuestionAssignedToCandidate(question._id, candidate._id) ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                  </span>
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <User className="h-4 w-4 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium truncate" title={candidate.name || candidate.email}>
                                        {candidate.name || candidate.email}
                                      </div>
                                      {candidate.email && candidate.name && (
                                        <div className="text-xs text-muted-foreground truncate" title={candidate.email}>
                                          {candidate.email}
                                        </div>
                                      )}
                                      <div className="flex items-center gap-1 mt-1">
                                        <Badge variant="outline" className="text-xs">
                                          {candidate.experience || 'N/A'} years
                                        </Badge>
                                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                          Eligible
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {/* No Results */}
                              {processedCandidatesData.candidates.length === 0 && (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                  No candidates available.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No eligible candidates available</p>
                  </div>
                )}

                {/* Assignment Summary for this Question */}
                <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <span className="font-medium">
                      {getAssignedCandidatesForQuestion(question._id).length} candidates
                    </span>
                    {' '}selected for this question
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overall Assignment Summary - Fixed at bottom */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex-shrink-0">
        <h4 className="font-medium text-blue-900 mb-2">Assignment Summary</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-blue-700">Total Questions:</span>
            <span className="font-medium ml-1">{selectedQuestions.length}</span>
          </div>
          <div>
            <span className="text-blue-700">Total Assignments:</span>
            <span className="font-medium ml-1">{getTotalAssignments()}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {currentStep === "banks" && (
              <>
                <Database className="h-5 w-5 text-blue-600" />
                Question Bank Selection
              </>
            )}
            {currentStep === "questions" && (
              <>
                <Database className="h-5 w-5 text-blue-600" />
                Select Questions
              </>
            )}
            {currentStep === "candidates" && (
              <>
                <Users className="h-5 w-5 text-blue-600" />
                Assign to Candidates
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 pr-2">
          {currentStep === "banks" ? renderBankSelection() : 
           currentStep === "questions" ? renderQuestionSelection() : 
           renderCandidateSelection()}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {currentStep === "banks" && (
            <Button 
              onClick={handleProceedToQuestions}
              disabled={selectedBankIds.length === 0}
              className="flex items-center gap-2"
            >
              Proceed to Questions
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {currentStep === "questions" && (
            <Button 
              onClick={handleProceedToCandidates}
              disabled={selectedQuestions.length === 0}
              className="flex items-center gap-2"
            >
              Assign to Candidates
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {currentStep === "candidates" && (
            <Button 
              onClick={handleFinalAssignment}
              disabled={getTotalAssignments() === 0}
              className="flex items-center gap-2"
            >
              Assign Questions ({getTotalAssignments()})
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionBankSelectionModal;